"use client";

import { isOwnDomainCitation } from "@repo/core/client";
import { applyKeyMessageHighlights } from "@shared/lib/applyKeyMessageHighlights";
import type { HighlightIndex } from "@shared/lib/buildKeyMessageHighlightIndex";
import {
	detectMarkdown,
	getPlatformDisplayName,
	getPlatformLogo,
	getPlatformOrder,
	highlightCompetitorMentions,
	isBrandMentionedInExecution,
	processDiscoveryResponse,
	renderMarkdownSync,
	transformRawGoogleAiOverview,
} from "@shared/lib/report-utils";
import { useTranslations } from "next-intl";
import Image from "next/image";
import Link from "next/link";
import { useEffect, useState } from "react";
import { GoogleAiOverviewSection } from "./GoogleAiOverviewSection";
import { GroundingSearchesSection } from "./GroundingSearchesSection";

interface DiscoveryQueryExecution {
	id: string;
	platform: string;
	model: string;
	browsing: boolean;
	answerSummary: string;
	ourBrandInSummary: boolean;
	ourBrandRankInSummary?: number;
	totalCitations: number;
	ourDomainCitations: number;
	completedAt?: string;
	error?: string;
	citations: Array<{
		id: string;
		url: string;
		title?: string;
		description?: string;
		isOurDomain: boolean;
		brandAttributed?: string;
	}>;
	brandMentions: Array<{
		id: string;
		brandName: string;
		isOurBrand: boolean;
		isCompetitor: boolean;
		inSummary: boolean;
		summaryRank?: number;
		context?: string;
	}>;
	googleAiOverview?: any;
	groundingSearches?: Array<{
		id: string;
		query: string;
		orderInResponse?: number;
		searchContext?: string;
		rankings: Array<{
			id: string;
			domain: string;
			isOurBrand: boolean;
			isCompetitor: boolean;
			rankPosition: number | null;
			url: string | null;
			title: string | null;
			description: string | null;
		}>;
	}>;
}

interface TabbedDiscoveryQueryProps {
	platformExecutions: DiscoveryQueryExecution[];
	competitorNames: string[];
	brandSnapshot?: any;
	propertyCompetitors?: any[];
	/** Property aliases for brand detection (e.g., ["Galleri", "Pathfinder"] for a property named "Grail") */
	propertyAliases?: string[];
	/**
	 * Whether this subject has a focal brand of its own. False for CATEGORY:
	 * the property IS the market, so there is no "you" to have been mentioned,
	 * cited or highlighted. When false the focal-brand identity is dropped
	 * before any matching runs — which also means the stored `isOurBrand` /
	 * `ourBrandInSummary` flags on rows written before the category fix (where
	 * the category's own name was used as the brand) are never believed.
	 * Tracked-brand (competitor) matching is untouched: for a category that IS
	 * the product.
	 */
	hasFocalSubject?: boolean;
	domain?: string;
	/**
	 * Per-platform / per-sentence key-message highlight index. When provided
	 * (BRAND/PRODUCT reports), matched sentences in each AI response are wrapped
	 * in purple (POSITIVE) / orange (NEGATIVE) marks. Absent for PERSON reports —
	 * those keep the existing TrackedAttributesSection.
	 */
	highlightIndex?: HighlightIndex;
	/**
	 * Platform tab to open on. Used by citation drill-ins that arrive knowing
	 * which platform produced the citation. Falls back to the default ordering
	 * when the platform isn't among the executions.
	 */
	initialPlatform?: string;
	/**
	 * When provided, each tab gets a link out to that execution's routed answer
	 * record. Optional on purpose: consumers that don't pass it — public report
	 * views, the design lab — render exactly as before.
	 */
	buildAnswerHref?: (executionId: string) => string;
	/**
	 * False for a report whose grounding searches were stored without rank
	 * checks (`GroundingSearchListV2`), so each answer's grounding searches show
	 * no rank, gap or "not ranked" states. Defaults to true: every other caller
	 * renders exactly as before.
	 */
	groundingRankTracking?: boolean;
}

// Priority platforms - ChatGPT and Google AI Overview first
const PRIORITY_PLATFORMS = ["openai", "google_ai_overview"];

export function TabbedDiscoveryQuery({
	platformExecutions,
	competitorNames,
	brandSnapshot,
	propertyCompetitors,
	propertyAliases,
	hasFocalSubject = true,
	domain,
	highlightIndex,
	initialPlatform,
	buildAnswerHref,
	groundingRankTracking = true,
}: TabbedDiscoveryQueryProps) {
	const t = useTranslations();

	// The identity used for focal-brand matching and highlighting. Empty for a
	// subject that has none, so `isOurBrand` (which re-matches live rather than
	// trusting stored flags) answers false everywhere: no green "Mentioned"
	// badge, no green tab dot, nothing highlighted as the brand.
	const focalBrandSnapshot = hasFocalSubject ? brandSnapshot : undefined;
	const focalPropertyAliases = hasFocalSubject ? propertyAliases : undefined;

	// Sort platforms with priority ones first
	const sortedExecutions = [...platformExecutions].sort((a, b) => {
		const aIsPriority = PRIORITY_PLATFORMS.includes(a.platform);
		const bIsPriority = PRIORITY_PLATFORMS.includes(b.platform);

		if (aIsPriority && !bIsPriority) {
			return -1;
		}
		if (!aIsPriority && bIsPriority) {
			return 1;
		}

		return getPlatformOrder(a.platform) - getPlatformOrder(b.platform);
	});

	// Default to the caller-requested platform when present, else the first
	// platform in priority order (ChatGPT if available)
	const [activeTab, setActiveTab] = useState(
		initialPlatform &&
			sortedExecutions.some((e) => e.platform === initialPlatform)
			? initialPlatform
			: sortedExecutions[0]?.platform || "",
	);

	// Store processed markdown for each execution
	const [processedContent, setProcessedContent] = useState<Map<string, any>>(
		new Map(),
	);

	// Process markdown for all executions
	useEffect(() => {
		const processAll = async () => {
			const processed = new Map();

			for (const execution of platformExecutions) {
				if (!execution.error && execution.answerSummary) {
					try {
						const result = await processDiscoveryResponse(
							{
								answer_summary: execution.answerSummary,
								brands_mentioned: execution.brandMentions || [],
								citations: execution.citations || [],
							},
							{
								competitorNames,
								brandSnapshot: focalBrandSnapshot,
								// Aliases drive our-brand highlighting; empty for a
								// subject with no focal brand.
								propertyAliases: focalPropertyAliases,
							},
						);
						// Post-process the rendered HTML to wrap key-message-matched
						// sentences (BRAND/PRODUCT reports only; index is empty for
						// PERSON reports). Done after the markdown / brand-mention
						// pipeline so the highlight marks compose cleanly with the
						// brand/competitor marks already in the HTML.
						if (highlightIndex && highlightIndex.size > 0) {
							result.htmlContent = applyKeyMessageHighlights(
								result.htmlContent,
								execution.platform,
								highlightIndex,
							);
						}
						processed.set(execution.id, result);
					} catch (error) {
						// Fallback to sync rendering
						processed.set(execution.id, {
							htmlContent: renderMarkdownSync(
								highlightCompetitorMentions(
									execution.answerSummary,
									competitorNames,
									focalBrandSnapshot,
									false,
									focalPropertyAliases,
								),
							),
							isMarkdown: detectMarkdown(execution.answerSummary),
							brandMentions: execution.brandMentions || [],
							citations: execution.citations || [],
						});
					}
				}
			}

			setProcessedContent(processed);
		};

		processAll();
	}, [
		platformExecutions,
		competitorNames,
		focalBrandSnapshot,
		focalPropertyAliases,
		highlightIndex,
	]);

	const activeExecution = sortedExecutions.find(
		(e) => e.platform === activeTab,
	);

	if (!activeExecution) {
		return null;
	}

	const processedResponse = processedContent.get(activeExecution.id);
	const activeBrandMentioned = isBrandMentionedInExecution(
		activeExecution,
		focalBrandSnapshot,
		focalPropertyAliases,
	);

	return (
		<div className="mt-4">
			{/* Platform Tabs */}
			<div className="border-b border-gray-200 mb-4">
				<nav
					className="flex space-x-1 overflow-x-auto"
					aria-label="Platform tabs"
				>
					{sortedExecutions.map((execution) => {
						const isActive = execution.platform === activeTab;
						const displayName = getPlatformDisplayName(
							execution.platform,
						);
						const logoPath = getPlatformLogo(execution.platform);
						const hasError = !!execution.error;
						const hasMention = isBrandMentionedInExecution(
							execution,
							focalBrandSnapshot,
							focalPropertyAliases,
						);
						const hasCitation = execution.ourDomainCitations > 0;

						return (
							<button
								key={execution.id}
								type="button"
								onClick={() => setActiveTab(execution.platform)}
								className={`flex items-center gap-2 px-3 py-2 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
									isActive
										? "border-blue-600 text-blue-600 bg-blue-50"
										: hasError
											? "border-transparent text-red-400 hover:text-red-500"
											: "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
								}`}
								aria-current={isActive ? "page" : undefined}
							>
								{logoPath && (
									<Image
										src={logoPath}
										alt={`${displayName} logo`}
										width={16}
										height={16}
										className="w-4 h-4"
									/>
								)}
								<span className="hidden sm:inline text-xs">
									{displayName}
								</span>
								{/* Mention / citation dots are a verdict about the
								    focal brand. A subject that has none gets no dot
								    rather than a gray "No mentions" one, which would
								    read as a negative finding. */}
								{!hasError && hasFocalSubject && (
									<span className="flex items-center gap-1">
										{hasMention && (
											<span
												className="w-2 h-2 bg-green-500 rounded-full"
												title="Mentioned"
											/>
										)}
										{hasCitation && (
											<span
												className="w-2 h-2 bg-blue-500 rounded-full"
												title="Cited"
											/>
										)}
										{!hasMention && !hasCitation && (
											<span
												className="w-2 h-2 bg-gray-300 rounded-full"
												title="No mentions"
											/>
										)}
									</span>
								)}
							</button>
						);
					})}
				</nav>
			</div>

			{/* Active Platform Content */}
			<div className="bg-gray-50 rounded-lg p-4">
				{buildAnswerHref && (
					<div className="mb-3 flex justify-end">
						<Link
							href={buildAnswerHref(activeExecution.id)}
							className="text-xs font-semibold text-primary hover:underline"
						>
							{t("properties.answerRecordPage.openRecord")}
						</Link>
					</div>
				)}
				{activeExecution.error ? (
					<div className="text-red-600 text-sm">
						<strong>Error:</strong> {activeExecution.error}
					</div>
				) : activeExecution.platform === "google_ai_overview" &&
					activeExecution.googleAiOverview ? (
					<GoogleAiOverviewSection
						data={transformRawGoogleAiOverview(
							activeExecution.googleAiOverview,
						)}
						brandSnapshot={brandSnapshot}
						propertyCompetitors={propertyCompetitors}
						propertyAliases={propertyAliases}
						hasFocalSubject={hasFocalSubject}
					/>
				) : (
					<div className="space-y-4">
						{/* Status Badges. Every one of them is about the focal
						    brand — whether it was mentioned, how many of its own
						    citations appeared — so the row is dropped entirely for
						    a subject that has no focal brand. The brands the answer
						    DID name are listed below. */}
						{hasFocalSubject && (
							<div className="flex items-center gap-2 flex-wrap">
								{activeBrandMentioned && (
									<span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
										✓ Mentioned
									</span>
								)}
								{activeExecution.ourDomainCitations > 0 && (
									<span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
										📎 {activeExecution.ourDomainCitations}{" "}
										citation
										{activeExecution.ourDomainCitations !==
										1
											? "s"
											: ""}
									</span>
								)}
								{!activeBrandMentioned &&
									activeExecution.ourDomainCitations ===
										0 && (
										<span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
											No mentions
										</span>
									)}
							</div>
						)}

						{/* Response Summary */}
						<div>
							<h5 className="font-medium text-gray-900 mb-2 text-sm">
								Response Summary
							</h5>
							{processedResponse ? (
								<div className="text-sm text-gray-700 bg-white p-3 rounded border">
									<div
										// biome-ignore lint/security/noDangerouslySetInnerHtml: LLM answer through renderMarkdownSync — the sanitizing renderer strips the highlight spans this view depends on; moving to a sanitized pipeline that preserves them is tracked follow-up work
										dangerouslySetInnerHTML={{
											__html: processedResponse.htmlContent,
										}}
										className="prose prose-sm max-w-none
                               prose-headings:font-bold prose-headings:text-gray-900
                               prose-h2:text-lg prose-h2:mt-4 prose-h2:mb-2
                               prose-h3:text-base prose-h3:mt-3 prose-h3:mb-1
                               prose-p:text-gray-700 prose-p:leading-relaxed prose-p:mb-2
                               prose-a:text-blue-600 prose-a:no-underline hover:prose-a:underline
                               prose-ul:list-disc prose-ul:ml-4 prose-ul:mb-3
                               prose-ol:list-decimal prose-ol:ml-4 prose-ol:mb-3
                               prose-li:mb-0.5
                               prose-strong:text-gray-900 prose-strong:font-semibold"
									/>
								</div>
							) : (
								<div className="bg-white p-3 rounded border">
									<div className="flex items-center gap-2 text-gray-500 text-sm">
										<div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-400" />
										<span>Processing...</span>
									</div>
								</div>
							)}
						</div>

						{/* Brand Mentions */}
						{processedResponse?.brandMentions &&
							processedResponse.brandMentions.length > 0 && (
								<div>
									<h5 className="font-medium text-gray-900 mb-2 text-sm">
										{/* With no focal brand these are simply the
										    tracked brands this answer named — there is
										    no "you" for them to be measured against. */}
										{hasFocalSubject
											? "Brands Mentioned ("
											: "Brands named ("}
										{processedResponse.brandMentions.length}
										)
									</h5>
									<div className="flex flex-wrap gap-1">
										{processedResponse.brandMentions
											.sort((a: any, b: any) => {
												if (
													a.type === "our-brand" &&
													b.type !== "our-brand"
												) {
													return -1;
												}
												if (
													a.type !== "our-brand" &&
													b.type === "our-brand"
												) {
													return 1;
												}
												return 0;
											})
											.slice(0, 10)
											.map(
												(
													mention: any,
													index: number,
												) => {
													const isOur =
														mention.type ===
														"our-brand";
													const isCompetitor =
														mention.type ===
														"competitor";
													return (
														<span
															key={
																mention.name +
																index
															}
															className={`px-2 py-1 rounded text-xs font-medium ${
																isOur
																	? "bg-green-100 text-green-800"
																	: isCompetitor
																		? "bg-orange-100 text-orange-800"
																		: "bg-gray-100 text-gray-700"
															}`}
														>
															{mention.name}
														</span>
													);
												},
											)}
										{processedResponse.brandMentions
											.length > 10 && (
											<span className="px-2 py-1 text-xs text-gray-500">
												+
												{processedResponse.brandMentions
													.length - 10}{" "}
												more
											</span>
										)}
									</div>
								</div>
							)}

						{/* Our Citations */}
						{activeExecution.ourDomainCitations > 0 && (
							<div>
								<h5 className="font-medium text-gray-900 mb-2 text-sm">
									Your Citations (
									{activeExecution.ourDomainCitations})
								</h5>
								<div className="space-y-1">
									{activeExecution.citations
										.filter((c) =>
											isOwnDomainCitation(
												c.isOurDomain,
												domain,
											),
										)
										.slice(0, 3)
										.map((citation) => (
											<div
												key={citation.id}
												className="text-sm bg-green-50 border border-green-200 p-2 rounded"
											>
												<a
													href={citation.url}
													target="_blank"
													rel="noopener noreferrer"
													className="font-medium text-green-700 hover:underline"
												>
													{citation.title ||
														citation.url}
												</a>
											</div>
										))}
								</div>
							</div>
						)}

						{/* Grounding Searches */}
						{activeExecution.groundingSearches &&
							activeExecution.groundingSearches.length > 0 && (
								<GroundingSearchesSection
									groundingSearches={
										activeExecution.groundingSearches
									}
									platformName={getPlatformDisplayName(
										activeExecution.platform,
									)}
									platform={activeExecution.platform}
									brandDomain={domain}
									rankTracking={groundingRankTracking}
								/>
							)}
					</div>
				)}
			</div>
		</div>
	);
}
